import React, { useState, useEffect } from "react";
import { wsService } from "../../../services/websocket";
import { ObsSettings as ObsSettingsType } from "../../../types";
import {
  getStoredPlayerId,
  getStoredObsSettings,
  setStoredObsSettings,
} from "../../../utils/storage";
import "./ObsSettings.css";

interface ObsSettingsProps {
  lobbyCode: string;
  isHost: boolean;
}

const DEFAULT_SETTINGS: ObsSettingsType = {
  bgOpacity: 100,
  bgColor: "#ffffff",
  borderRadius: 0,
  textColor: "#000000",
  borderColor: "#333333",
  winningColor: "#4caf50",
  fontSize: 25,
  fontWeight: 800,
};

export const ObsSettings: React.FC<ObsSettingsProps> = ({
  lobbyCode,
  isHost,
}) => {
  const [settings, setSettings] = useState<ObsSettingsType>(DEFAULT_SETTINGS);
  const [isLoading, setIsLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  // Load settings from backend on mount, but use localStorage as initial value
  useEffect(() => {
    const loadSettings = async () => {
      try {
        // First, try to load from localStorage for immediate display
        const cached = getStoredObsSettings();
        if (cached) {
          setSettings(cached);
        }

        // Ensure WebSocket is connected before loading from backend
        await wsService.connect();

        // Then load from backend (which may override cached values)
        const response = await wsService.getObsSettings({ lobbyCode });
        setSettings(response.settings);

        // Update localStorage with backend values
        setStoredObsSettings(response.settings);
      } catch (error) {
        console.error("Failed to load OBS settings:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadSettings();
  }, [lobbyCode]);

  // Listen for settings updates from other clients
  useEffect(() => {
    const unsubscribe = wsService.subscribe((message) => {
      if (message.type === "obs_settings_update") {
        const payload = message.payload as { settings: ObsSettingsType };
        setSettings(payload.settings);
      }
    });

    return unsubscribe;
  }, []);

  const handleChange = (key: keyof ObsSettingsType, value: number | string) => {
    if (!isHost) return; // Only host can change settings

    const newSettings = { ...settings, [key]: value };
    setSettings(newSettings);

    // Save to localStorage for future lobbies
    setStoredObsSettings(newSettings);

    // Send to backend immediately
    wsService.updateObsSettings({
      lobbyCode,
      settings: newSettings,
    });
  };

  const handleReset = () => {
    if (!isHost) return; // Only host can reset settings

    setSettings(DEFAULT_SETTINGS);

    // Save reset values to localStorage
    setStoredObsSettings(DEFAULT_SETTINGS);

    wsService.updateObsSettings({
      lobbyCode,
      settings: DEFAULT_SETTINGS,
    });
  };

  const handleCopyObsLink = () => {
    const playerId = getStoredPlayerId(lobbyCode);
    const obsUrl = `${window.location.origin}/obs/${lobbyCode}/${playerId}`;
    navigator.clipboard
      .writeText(obsUrl)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 3000);
      })
      .catch(() => {
        console.error("Failed to copy OBS link");
      });
  };

  if (isLoading) {
    return (
      <div className="obs-settings">
        <p className="obs-settings__description">Lade Einstellungen...</p>
      </div>
    );
  }

  return (
    <div className="obs-settings">
      <h3 className="obs-settings__title">OBS Einstellungen</h3>
      <p className="obs-settings__description">
        Passe das Aussehen der OBS Browser-Quelle an. Die Einstellungen werden
        automatisch gespeichert und auf alle OBS Browser Sources angewendet.
      </p>

      {!isHost && (
        <div className="obs-settings__info-box">
          ℹ️ Nur der Host kann die OBS-Einstellungen ändern.
        </div>
      )}

      <button
        onClick={handleCopyObsLink}
        className={`obs-settings__copy-link-btn ${
          copied ? "obs-settings__copy-link-btn--copied" : ""
        }`}
      >
        {copied ? "✓ Copied!" : "📋 OBS Link kopieren"}
      </button>

      <div className="obs-settings__controls">
        <div className="obs-settings__control">
          <label className="obs-settings__label">
            <span className="obs-settings__label-text">
              Hintergrund Transparenz
            </span>
            <span className="obs-settings__value">{settings.bgOpacity}%</span>
          </label>
          <input
            type="range"
            min="0"
            max="100"
            value={settings.bgOpacity}
            onChange={(e) =>
              handleChange("bgOpacity", parseInt(e.target.value))
            }
            className="obs-settings__slider"
            disabled={!isHost}
          />
        </div>

        <div className="obs-settings__control">
          <label className="obs-settings__label">
            <span className="obs-settings__label-text">Hintergrundfarbe</span>
          </label>
          <div className="obs-settings__color-input">
            <input
              type="color"
              value={settings.bgColor}
              onChange={(e) => handleChange("bgColor", e.target.value)}
              className="obs-settings__color-picker"
              disabled={!isHost}
            />
            <input
              type="text"
              value={settings.bgColor}
              onChange={(e) => handleChange("bgColor", e.target.value)}
              className="obs-settings__color-text"
              placeholder="#ffffff"
              disabled={!isHost}
            />
          </div>
        </div>

        <div className="obs-settings__control">
          <label className="obs-settings__label">
            <span className="obs-settings__label-text">Border Radius</span>
            <span className="obs-settings__value">
              {settings.borderRadius}px
            </span>
          </label>
          <input
            type="range"
            min="0"
            max="30"
            value={settings.borderRadius}
            onChange={(e) =>
              handleChange("borderRadius", parseInt(e.target.value))
            }
            className="obs-settings__slider"
            disabled={!isHost}
          />
        </div>

        <div className="obs-settings__control">
          <label className="obs-settings__label">
            <span className="obs-settings__label-text">Textfarbe</span>
          </label>
          <div className="obs-settings__color-input">
            <input
              type="color"
              value={settings.textColor}
              onChange={(e) => handleChange("textColor", e.target.value)}
              className="obs-settings__color-picker"
              disabled={!isHost}
            />
            <input
              type="text"
              value={settings.textColor}
              onChange={(e) => handleChange("textColor", e.target.value)}
              className="obs-settings__color-text"
              placeholder="#000000"
              disabled={!isHost}
            />
          </div>
        </div>

        <div className="obs-settings__control">
          <label className="obs-settings__label">
            <span className="obs-settings__label-text">Rahmenfarbe</span>
          </label>
          <div className="obs-settings__color-input">
            <input
              type="color"
              value={settings.borderColor}
              onChange={(e) => handleChange("borderColor", e.target.value)}
              className="obs-settings__color-picker"
              disabled={!isHost}
            />
            <input
              type="text"
              value={settings.borderColor}
              onChange={(e) => handleChange("borderColor", e.target.value)}
              className="obs-settings__color-text"
              placeholder="#333333"
              disabled={!isHost}
            />
          </div>
        </div>

        <div className="obs-settings__control">
          <label className="obs-settings__label">
            <span className="obs-settings__label-text">Gewinn-Farbe</span>
          </label>
          <div className="obs-settings__color-input">
            <input
              type="color"
              value={settings.winningColor}
              onChange={(e) => handleChange("winningColor", e.target.value)}
              className="obs-settings__color-picker"
              disabled={!isHost}
            />
            <input
              type="text"
              value={settings.winningColor}
              onChange={(e) => handleChange("winningColor", e.target.value)}
              className="obs-settings__color-text"
              placeholder="#4caf50"
              disabled={!isHost}
            />
          </div>
        </div>

        <div className="obs-settings__control">
          <label className="obs-settings__label">
            <span className="obs-settings__label-text">Schriftgröße</span>
            <span className="obs-settings__value">{settings.fontSize}px</span>
          </label>
          <input
            type="range"
            min="12"
            max="60"
            value={settings.fontSize}
            onChange={(e) => handleChange("fontSize", parseInt(e.target.value))}
            className="obs-settings__slider"
            disabled={!isHost}
          />
        </div>

        <div className="obs-settings__control">
          <label className="obs-settings__label">
            <span className="obs-settings__label-text">Schriftstärke</span>
            <span className="obs-settings__value">{settings.fontWeight}</span>
          </label>
          <input
            type="range"
            min="100"
            max="900"
            step="100"
            value={settings.fontWeight}
            onChange={(e) =>
              handleChange("fontWeight", parseInt(e.target.value))
            }
            className="obs-settings__slider"
            disabled={!isHost}
          />
        </div>

        <button
          onClick={handleReset}
          className="obs-settings__reset-btn"
          disabled={!isHost}
        >
          Zurücksetzen
        </button>
      </div>
    </div>
  );
};
